import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const FILTERS = [
  { id: 'all',      label: 'All' },
  { id: 'unread',   label: 'Unread' },
  { id: 'open',     label: 'Open' },
  { id: 'archived', label: 'Archived' },
];

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get('/api/admin/messages', { headers: authHeaders() })
      .then(({ data }) => alive && setItems(Array.isArray(data) ? data : []))
      .catch((e) => alive && setErr(e?.response?.data?.message || 'Failed to load'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'unread')   return items.filter((m) => !m.read && m.status !== 'archived');
    if (filter === 'open')     return items.filter((m) => m.status !== 'archived');
    if (filter === 'archived') return items.filter((m) => m.status === 'archived');
    return items;
  }, [filter, items]);

  const patch = async (m, body) => {
    try {
      const { data } = await axios.patch(`/api/admin/messages/${m._id}`, body, { headers: authHeaders() });
      setItems((arr) => arr.map((x) => (x._id === m._id ? data : x)));
    } catch (e) {
      setErr(e?.response?.data?.message || 'Update failed');
    }
  };

  const onDelete = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`/api/admin/messages/${m._id}`, { headers: authHeaders() });
      setItems((arr) => arr.filter((x) => x._id !== m._id));
    } catch (e) {
      setErr(e?.response?.data?.message || 'Delete failed');
    }
  };

  const [replyDrafts, setReplyDrafts] = useState({}); // { id: 'text' }
  const [sendingId, setSendingId] = useState(null);
  const [toast, setToast] = useState('');
  const ping = (t) => { setToast(t); setTimeout(() => setToast(''), 2200); };

  const expand = (m) => {
    setOpenId(openId === m._id ? null : m._id);
    if (!m.read) patch(m, { read: true });
  };

  const sendReply = async (m) => {
    const body = (replyDrafts[m._id] || '').trim();
    if (!body) return;
    setSendingId(m._id);
    setErr('');
    try {
      const { data } = await axios.post(
        `/api/admin/messages/${m._id}/reply`,
        { body },
        { headers: authHeaders(), timeout: 15000 }
      );
      setItems((arr) => arr.map((x) => (x._id === m._id ? data.message : x)));
      setReplyDrafts((d) => { const c = { ...d }; delete c[m._id]; return c; });
      ping(data.devMode ? 'Reply saved (mail in dev-mode — check server logs).' : `Reply sent to ${m.email}.`);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Reply failed.');
    } finally {
      setSendingId(null);
    }
  };

  const unread = items.filter((m) => !m.read && m.status !== 'archived').length;

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1 className="admin-page-title">Messages</h1>
        <p className="admin-page-sub">Contact-page inbox · {items.length} total · {unread} unread. New-release emails are managed separately.</p>
      </header>

      <div className="admin-toolbar">
        <div style={{ display: 'inline-flex', gap: 6, padding: 4, background: 'var(--admin-panel-bg)', border: '1px solid rgba(184,134,11,0.3)', borderRadius: 999 }}>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              style={{
                padding: '6px 14px', border: 0, borderRadius: 999, cursor: 'pointer',
                background: filter === f.id ? 'linear-gradient(135deg, #b8860b, #daa520)' : 'transparent',
                color: filter === f.id ? '#1a1207' : 'var(--admin-fg-muted)',
                fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0.5, textTransform: 'uppercase',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {err && <div className="admin-toast admin-toast-error">{err}</div>}
      {toast && <div className="admin-toast">{toast}</div>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>From</th>
              <th>Subject</th>
              <th>Received</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, opacity: 0.7 }}>No messages.</td></tr>
            ) : filtered.map((m) => {
              const open = openId === m._id;
              return (
                <React.Fragment key={m._id}>
                  <tr style={{ cursor: 'pointer', fontWeight: m.read ? 400 : 600 }} onClick={() => expand(m)}>
                    <td style={{ width: 32 }}>{!m.read && <span className="admin-tag admin-tag-new">new</span>}</td>
                    <td>
                      <div style={{ color: 'var(--admin-fg-strong)' }}>{m.name}</div>
                      <div style={{ fontSize: '0.78rem', opacity: 0.6 }}>{m.email}</div>
                    </td>
                    <td>{m.subject || '—'}</td>
                    <td style={{ fontSize: '0.82rem', opacity: 0.75 }}>{m.createdAt ? new Date(m.createdAt).toLocaleString() : '—'}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="admin-row-actions">
                        <a className="admin-btn admin-btn-ghost admin-btn-sm" href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + (m.subject || ''))}`}>Reply</a>
                        {m.status === 'archived'
                          ? <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => patch(m, { status: 'open' })}>Unarchive</button>
                          : <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => patch(m, { status: 'archived' })}>Archive</button>}
                        <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(m)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--admin-row-expand-bg)', padding: '14px 18px' }}>
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: '0.72rem', letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--admin-fg-faint)', marginBottom: 4 }}>Their message</div>
                          <p style={{ margin: 0, whiteSpace: 'pre-line', lineHeight: 1.65 }}>{m.message}</p>
                        </div>

                        {m.repliedAt ? (
                          <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: 'rgba(72, 168, 142, 0.12)', border: '1px solid rgba(72, 168, 142, 0.35)' }}>
                            <div style={{ fontSize: '0.72rem', letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--admin-fg-faint)', marginBottom: 4 }}>
                              Replied {new Date(m.repliedAt).toLocaleString()}
                            </div>
                            <p style={{ margin: 0, whiteSpace: 'pre-line', lineHeight: 1.65 }}>{m.replyBody}</p>
                          </div>
                        ) : null}

                        <div style={{ marginTop: 14 }}>
                          <div style={{ fontSize: '0.72rem', letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--admin-fg-faint)', marginBottom: 4 }}>
                            {m.repliedAt ? 'Reply again' : 'Reply'}
                          </div>
                          <textarea
                            className="admin-textarea"
                            rows={4}
                            placeholder={`Reply to ${m.email}…`}
                            value={replyDrafts[m._id] || ''}
                            onChange={(e) => setReplyDrafts((d) => ({ ...d, [m._id]: e.target.value }))}
                            disabled={sendingId === m._id}
                          />
                          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.78rem', color: 'var(--admin-fg-faint)' }}>
                              Sends from your configured GMAIL_USER address.
                            </span>
                            <div style={{ display: 'inline-flex', gap: 8 }}>
                              <a className="admin-btn admin-btn-ghost admin-btn-sm" href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + (m.subject || ''))}`}>Use my mail app instead</a>
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => sendReply(m)}
                                disabled={sendingId === m._id || !(replyDrafts[m._id] || '').trim()}
                              >
                                {sendingId === m._id ? 'Sending…' : 'Send reply'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
